import { AlertCircle, Info, Pencil } from "lucide-react";
import type { ReactNode } from "react";
import { useMemo } from "react";

import ScorecardStaleBanner from "../../ScorecardStaleBanner";
import ScorecardRowFilterBar from "../Rows/ScorecardRowFilterBar";
import ScorecardRowResultsList, {
  type ScorecardRowResultItem,
} from "../Rows/ScorecardRowResultsList";
import { SCORECARD_DETAIL_TITLE } from "../../constants";
import {
  COLUMN_AGGREGATE_TYPE_LABELS,
  type ColumnAggregateType,
  DEFAULT_COLUMN_AGGREGATE_PERCENTILE,
  columnAggregateUsesExpectedColumn,
  getScorecardColumnAggregateType,
  isColumnAggregateType,
} from "../../criterionConfig";
import {
  buildVerdictFilterCounts,
  countCriterionRowsByFilter,
  formatColumnAggregateResult,
  formatPercentileLabel,
  formatScorecardScore,
  getColumnAggregateTopValues,
  getCriterionMetadataLabel,
  getDisplayableEvaluatorEvidence,
  getScorecardCriterionRowItems,
  getScorecardRowsEmptyMessage,
  isColumnAggregatePrimitive,
  rowMatchesSearchQuery,
} from "../../utils";
import type { ScorecardCriterionRowFilter, ScorecardCriterionRowsListState } from "../../types";
import ScorecardAssertionResults from "../ScorecardAssertionResults";

import ClassificationReport from "./ClassificationReport";
import ScorecardScoreSummary from "./ScorecardScoreSummary";
import ScorecardScoreDeltaBadge from "./ScorecardScoreDeltaBadge";
import ScorecardRerunTextButton from "./ScorecardRerunTextButton";
import ScorecardSectionBlockHeader from "./ScorecardSectionBlockHeader";

import { Button } from "@/components/ui/button";
import {
  OverflowDirection,
  OverflowDisplayTooltip,
} from "@/components/ui/Tooltip/OverflowDisplayTooltip";
import { cn } from "@/lib/utils";
import type {
  CriterionSummary,
  GetScorecardRowResponse,
  ScorecardRowSummary,
  ScorecardStep,
} from "@/queries/smart-tables/scorecard";
import { ScoreVerdict } from "@/queries/smart-tables/scorecard";
import type { SmartCell, SmartColumn } from "@/queries/smart-tables/types";

interface ScorecardCriterionDetailViewProps {
  step: ScorecardStep;
  summary?: CriterionSummary;
  rows: ScorecardRowSummary[];
  /** Server-computed step-verdict counts across the whole calculation. */
  verdictCounts?: Record<string, number>;
  rowBreakdowns: Record<number, GetScorecardRowResponse | undefined>;
  columns: SmartColumn[];
  totalRows?: number;
  getRowLabel?: (rowIndex: number) => string;
  /**
   * The persistent evaluator switcher, rendered by the parent so it stays
   * mounted across scope changes. Falls back to a plain title when there's
   * nothing to switch between (e.g. a single row evaluator).
   */
  titleSlot?: ReactNode;
  rowListState: ScorecardCriterionRowsListState;
  onRowListStateChange: (next: Partial<ScorecardCriterionRowsListState>) => void;
  onOpenRow: (rowIndex: number) => void;
  onEditCriterion: () => void;
  onRerunRows: (rowIndices?: number[]) => void;
  fallbackRerunRowIndices: number[];
  isRerunning: boolean;
  isRerunDisabled: boolean;
  isLoadingRows?: boolean;
  onLoadMoreRows?: () => void;
  hasMoreRows?: boolean;
  isLoadingMoreRows?: boolean;
  /**
   * When true, a stale banner with its own "Re-run" action is already shown
   * above this view for the same criterion -- the header's Re-run button is
   * hidden in that case to avoid offering the same action twice at once.
   */
  isStale?: boolean;
  getBackingCell?: (
    rowIndex: number,
    backingColumnId: string | null | undefined,
  ) => SmartCell | undefined;
  isReadOnly?: boolean;
}

const getNumberField = (
  record: Record<string, unknown> | null | undefined,
  key: string,
): number | null => {
  const value = record?.[key];
  return typeof value === "number" && Number.isFinite(value) ? value : null;
};

const getStringField = (
  record: Record<string, unknown> | null | undefined,
  key: string,
): string | null => {
  const value = record?.[key];
  return typeof value === "string" && value.length > 0 ? value : null;
};

const formatPercent = (value: number): string => formatScorecardScore(value);

const formatShortNumber = (value: number): string =>
  new Intl.NumberFormat(undefined, { maximumFractionDigits: 2 }).format(value);

const renderAggregateValueTooltip = (value: string) => (
  <div className="max-h-80 max-w-[520px] overflow-y-auto whitespace-pre-wrap break-words text-left text-xs leading-5">
    {value}
  </div>
);

const COLUMN_AGGREGATE_UNSCORABLE_EVIDENCE = "Source value was missing, blank, or not scorable.";

const formatColumnAggregateRawValue = (value: unknown): string | null => {
  if (value === null || value === undefined) {
    return null;
  }
  if (typeof value === "string") {
    const trimmed = value.trim();
    return trimmed.length > 0 ? trimmed : null;
  }
  if (typeof value === "number") {
    return Number.isFinite(value) ? formatShortNumber(value) : null;
  }
  if (typeof value === "boolean") {
    return value ? "true" : "false";
  }
  try {
    return JSON.stringify(value);
  } catch {
    return String(value);
  }
};

const getColumnAggregateRowEvidence = (
  stepResult: { evidence?: string | null; raw_value: unknown },
  sourceColumnTitle: string,
  { preferEvidence = false }: { preferEvidence?: boolean } = {},
): string | null => {
  const evidence = getDisplayableEvaluatorEvidence(stepResult.evidence);
  const usableEvidence = evidence === COLUMN_AGGREGATE_UNSCORABLE_EVIDENCE ? null : evidence;
  // Evidence for expected-column metrics names both values, e.g. "Predicted a · expected b".
  if (preferEvidence && usableEvidence) {
    return usableEvidence;
  }
  const rawValueLabel = formatColumnAggregateRawValue(stepResult.raw_value);
  if (rawValueLabel) {
    return `${sourceColumnTitle}: ${rawValueLabel}`;
  }
  return usableEvidence;
};

const CRITERION_ROW_FILTER_OPTIONS: {
  id: ScorecardCriterionRowFilter;
  label: string;
  verdict?: string;
}[] = [
  { id: "all", label: "All" },
  { id: "fail", label: "Failed", verdict: ScoreVerdict.FAIL },
  { id: "warn", label: "Review", verdict: ScoreVerdict.WARN },
  { id: "pass", label: "Passed", verdict: ScoreVerdict.PASS },
  { id: "error", label: "Errors", verdict: ScoreVerdict.ERROR },
];

/**
 * Column aggregate is a scorecard-level (top-level) evaluator: its result is
 * a single summary computed once across the whole column, scoped to the
 * scorecard/table. It still shows the contributing rows so drilling into a
 * column evaluator feels consistent with row-scoped evaluators.
 */
const ColumnAggregateCriterionDetail = ({
  step,
  summary,
  columns,
  rowResultItems,
  filterCounts,
  rowListState,
  onRowListStateChange,
  onOpenRow,
  onEditCriterion,
  onRerun,
  isRerunning,
  isRerunDisabled,
  isLoadingRows = false,
  onLoadMoreRows,
  hasMoreRows = false,
  isLoadingMoreRows = false,
  isStale = false,
  isReadOnly = false,
  titleSlot,
}: {
  step: ScorecardStep;
  summary?: CriterionSummary;
  columns: SmartColumn[];
  rowResultItems: ScorecardRowResultItem[];
  filterCounts: Record<ScorecardCriterionRowFilter, number>;
  rowListState: ScorecardCriterionRowsListState;
  onRowListStateChange: (next: Partial<ScorecardCriterionRowsListState>) => void;
  onOpenRow: (rowIndex: number) => void;
  onEditCriterion: () => void;
  onRerun: () => void;
  isRerunning: boolean;
  isRerunDisabled: boolean;
  isLoadingRows?: boolean;
  onLoadMoreRows?: () => void;
  hasMoreRows?: boolean;
  isLoadingMoreRows?: boolean;
  isStale?: boolean;
  isReadOnly?: boolean;
  titleSlot?: React.ReactNode;
}) => {
  const aggregateDisplay = formatColumnAggregateResult(
    summary?.aggregate_result,
    step.primitive_config?.aggregation_type as string | undefined,
  );
  const scoreFallbackDisplay =
    !aggregateDisplay && typeof summary?.score === "number"
      ? {
          primary: formatScorecardScore(summary.score),
          secondary: "Score value from the latest calculation",
        }
      : null;
  const display = aggregateDisplay ?? scoreFallbackDisplay;
  const topValues =
    summary?.aggregate_result?.aggregation_type === "most_frequent_value"
      ? getColumnAggregateTopValues(summary?.aggregate_result)
      : [];
  const metadataLabel = getCriterionMetadataLabel(step, columns, { isColumnAggregate: true });
  const aggregateResult = summary?.aggregate_result ?? null;
  const primitiveConfig = step.primitive_config ?? {};

  const aggregateTypeFromResult = getStringField(aggregateResult, "aggregation_type");
  const aggregateTypeFromConfig = getStringField(primitiveConfig, "aggregation_type");

  let resolvedAggregateType: ColumnAggregateType | null = null;
  if (isColumnAggregateType(aggregateTypeFromResult)) {
    resolvedAggregateType = aggregateTypeFromResult;
  } else if (isColumnAggregateType(aggregateTypeFromConfig)) {
    resolvedAggregateType = aggregateTypeFromConfig;
  }

  const sourceColumnId =
    step.source_column_ids[0] ?? getStringField(primitiveConfig, "source") ?? "";
  const sourceColumnTitle =
    columns.find(column => column.id === sourceColumnId)?.title ?? (sourceColumnId || "—");

  const totalRows = getNumberField(aggregateResult, "total");
  const scoredRows = getNumberField(aggregateResult, "scored_rows");
  const excludedRows = getNumberField(aggregateResult, "excluded_rows");
  const winnerCount = getNumberField(aggregateResult, "count");
  const winnerPercentage = getNumberField(aggregateResult, "percentage");
  const averageValue =
    getNumberField(aggregateResult, "average") ?? getNumberField(aggregateResult, "value");
  const aggregateScore = getNumberField(aggregateResult, "score") ?? summary?.score ?? null;
  const scoreMax = getNumberField(primitiveConfig, "score_max");
  const scoreMode =
    getStringField(aggregateResult, "score_mode") ??
    getStringField(primitiveConfig, "score_mode") ??
    "value";

  const getColumnTitle = (columnId: string | null) =>
    columnId ? (columns.find(column => column.id === columnId)?.title ?? columnId) : null;
  const labelSourceTitle = getColumnTitle(getStringField(primitiveConfig, "label_source"));
  const expectedSourceTitle = getColumnTitle(getStringField(primitiveConfig, "expected_source"));

  let rowsUsed: string | null = null;
  if (scoredRows !== null && totalRows !== null) {
    rowsUsed = `${scoredRows}/${totalRows} rows`;
  } else if (scoredRows !== null) {
    rowsUsed = `${scoredRows} rows`;
  } else if (totalRows !== null) {
    rowsUsed = `${totalRows} rows`;
  }

  const rowsUsedWithSkips =
    rowsUsed && excludedRows !== null && excludedRows > 0
      ? `${rowsUsed} (${excludedRows} skipped)`
      : rowsUsed;

  let scoreContribution = "Display only — does not affect total score.";
  if (
    resolvedAggregateType === "average_value" ||
    resolvedAggregateType === "most_frequent_value" ||
    resolvedAggregateType === "classification_report"
  ) {
    if (scoreMode === "score") {
      if (
        resolvedAggregateType === "average_value" &&
        averageValue !== null &&
        scoreMax !== null &&
        aggregateScore !== null
      ) {
        scoreContribution = `Included in total score: ${formatShortNumber(averageValue)} ÷ ${formatShortNumber(scoreMax)} = ${formatScorecardScore(aggregateScore)}`;
      } else if (resolvedAggregateType === "most_frequent_value" && winnerPercentage !== null) {
        scoreContribution = `Included in total score as winner frequency (${formatPercent(winnerPercentage)}).`;
      } else if (resolvedAggregateType === "classification_report" && aggregateScore !== null) {
        scoreContribution = `Included in total score as accuracy (${formatPercent(aggregateScore)}).`;
      } else {
        scoreContribution = "Included in total score.";
      }
    }
  }

  const calculationMeta: { label: string; value: string }[] = [
    {
      label: "Method",
      value: resolvedAggregateType
        ? COLUMN_AGGREGATE_TYPE_LABELS[resolvedAggregateType]
        : "Column evaluator",
    },
    { label: "Column", value: sourceColumnTitle },
    ...(resolvedAggregateType &&
    columnAggregateUsesExpectedColumn(resolvedAggregateType) &&
    expectedSourceTitle
      ? [{ label: "Expected", value: expectedSourceTitle }]
      : []),
    ...(resolvedAggregateType === "percentile_value"
      ? [
          {
            label: "Percentile",
            value: formatPercentileLabel(
              aggregateResult?.percentile ??
                primitiveConfig.percentile ??
                DEFAULT_COLUMN_AGGREGATE_PERCENTILE,
            ),
          },
        ]
      : []),
    { label: "Score impact", value: scoreContribution },
  ];

  if (rowsUsedWithSkips) {
    calculationMeta.push({ label: "Rows used", value: rowsUsedWithSkips });
  }

  if (resolvedAggregateType === "most_frequent_value" && winnerCount !== null) {
    calculationMeta.push({
      label: "Winning value support",
      value:
        totalRows !== null
          ? `${winnerCount}/${totalRows} rows${winnerPercentage !== null ? ` (${formatPercent(winnerPercentage)})` : ""}`
          : `${winnerCount} rows${winnerPercentage !== null ? ` (${formatPercent(winnerPercentage)})` : ""}`,
    });
  }

  if (
    (resolvedAggregateType === "min_value" || resolvedAggregateType === "max_value") &&
    labelSourceTitle
  ) {
    calculationMeta.push({ label: "Winner label", value: labelSourceTitle });
  }

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-start justify-between gap-2">
        <div className="min-w-0 flex-1 space-y-1">
          {titleSlot ?? <h3 className={SCORECARD_DETAIL_TITLE}>{step.title}</h3>}
          <p className="line-clamp-2 break-words text-sm leading-5 text-muted-foreground">
            Column evaluator
            {metadataLabel ? (
              <>
                {" · "}
                <span className="font-medium text-foreground">{metadataLabel}</span>
              </>
            ) : null}
          </p>
        </div>
        <div className="ml-auto flex shrink-0 items-center gap-1">
          {!isReadOnly && (
            <Button
              variant="ghost"
              size="sm"
              className="h-6 px-1.5 text-[11px] font-medium text-muted-foreground hover:text-foreground"
              onClick={onEditCriterion}
            >
              <Pencil className="mr-1 h-3 w-3" />
              Edit
            </Button>
          )}
          {display && !isStale && !isReadOnly ? (
            <ScorecardRerunTextButton
              onClick={onRerun}
              isRerunning={isRerunning}
              isDisabled={isRerunDisabled}
            />
          ) : null}
        </div>
      </div>

      {display ? (
        <div className="rounded-lg border border-border/70 bg-muted/10 px-4 py-5 text-center">
          <div className="flex min-w-0 items-center justify-center gap-2">
            <OverflowDisplayTooltip
              content={renderAggregateValueTooltip(display.primary)}
              overflowDirection={OverflowDirection.VERTICAL}
              lineClamp={2}
              side="top"
              className="mx-auto max-w-full break-words text-center text-2xl font-semibold leading-tight tracking-tight text-foreground sm:text-3xl"
              styles={{ wordBreak: "break-word" }}
            >
              <span>{display.primary}</span>
            </OverflowDisplayTooltip>
            <ScorecardScoreDeltaBadge drift={summary?.drift} />
          </div>
          {display.secondary ? (
            <p className="mt-1 text-sm text-muted-foreground">{display.secondary}</p>
          ) : null}
        </div>
      ) : (
        <ScorecardStaleBanner
          variant="first-run"
          message="Not calculated yet — run this evaluator to calculate its value."
          actionLabel="Run evaluator"
          loadingLabel="Running…"
          onRecalculate={onRerun}
          isRecalculating={isRerunning}
          isDisabled={isRerunDisabled || isReadOnly}
        />
      )}

      {resolvedAggregateType === "classification_report" ? (
        <ClassificationReport aggregateResult={aggregateResult} />
      ) : null}

      <div className="space-y-3">
        <div className="space-y-1">
          <div className="flex items-center justify-between gap-2">
            <h4 className="text-xs font-medium text-foreground">Rows</h4>
            {rowsUsedWithSkips ? (
              <span className="shrink-0 text-xs text-muted-foreground">{rowsUsedWithSkips}</span>
            ) : null}
          </div>
          <p className="text-xs leading-5 text-muted-foreground">
            These rows contributed source values to the column result above.
          </p>
        </div>

        <ScorecardRowFilterBar
          search={rowListState.search}
          onSearchChange={value => onRowListStateChange({ search: value })}
          filter={rowListState.filter}
          onFilterChange={value => onRowListStateChange({ filter: value })}
          options={CRITERION_ROW_FILTER_OPTIONS}
          counts={filterCounts}
        />

        <ScorecardRowResultsList
          items={rowResultItems}
          onOpenRow={onOpenRow}
          isLoading={isLoadingRows}
          onLoadMore={onLoadMoreRows}
          hasMore={hasMoreRows}
          isLoadingMore={isLoadingMoreRows}
          loadingMessage="Loading contributing rows…"
          emptyMessage={getScorecardRowsEmptyMessage(
            rowListState.filter,
            rowListState.search.trim().length > 0,
          )}
        />
      </div>

      <div className="rounded-lg border border-border/70 bg-background px-3 py-3">
        <div className="flex items-center gap-1.5 text-xs font-medium text-foreground">
          <Info className="h-3.5 w-3.5 text-muted-foreground" />
          How this evaluator is calculated
        </div>
        <dl className="mt-2 space-y-1.5 text-xs leading-5">
          {calculationMeta.map(item => (
            <div key={item.label} className="flex items-start gap-2">
              <dt className="w-24 shrink-0 text-muted-foreground">{item.label}</dt>
              <dd className="min-w-0 flex-1 text-foreground">{item.value}</dd>
            </div>
          ))}
        </dl>
      </div>

      {topValues.length > 0 ? (
        <div className="rounded-lg border border-border/70 bg-background">
          <div className="border-b border-border/60 px-3 py-2 text-xs font-medium text-muted-foreground">
            Top contributing values
          </div>
          {topValues.map(entry => (
            <div
              key={entry.value}
              className="flex items-start justify-between gap-3 border-b border-border/60 px-3 py-2.5 text-sm last:border-b-0"
            >
              <OverflowDisplayTooltip
                content={renderAggregateValueTooltip(entry.value)}
                overflowDirection={OverflowDirection.VERTICAL}
                lineClamp={2}
                side="top"
                className="min-w-0 flex-1 break-words font-medium leading-5 text-foreground"
                styles={{ wordBreak: "break-word" }}
              >
                <span>{entry.value}</span>
              </OverflowDisplayTooltip>
              <span className="shrink-0 text-xs text-muted-foreground">
                {entry.count} row{entry.count === 1 ? "" : "s"}
                {typeof entry.percentage === "number"
                  ? ` · ${formatPercent(entry.percentage)}`
                  : ""}
              </span>
            </div>
          ))}
        </div>
      ) : null}
    </div>
  );
};

const ScorecardCriterionDetailView = ({
  step,
  summary,
  rows,
  verdictCounts,
  rowBreakdowns,
  columns,
  totalRows,
  getRowLabel,
  titleSlot,
  rowListState,
  onRowListStateChange,
  onOpenRow,
  onEditCriterion,
  onRerunRows,
  fallbackRerunRowIndices,
  isRerunning,
  isRerunDisabled,
  isLoadingRows = false,
  onLoadMoreRows,
  hasMoreRows = false,
  isLoadingMoreRows = false,
  isStale = false,
  getBackingCell,
  isReadOnly = false,
}: ScorecardCriterionDetailViewProps) => {
  const isColumnAggregate = isColumnAggregatePrimitive(step.primitive_type);
  const effectiveIsStale = isStale && !isRerunning;

  const criterionRowItems = useMemo(
    () => getScorecardCriterionRowItems({ rows, rowBreakdowns, step, getBackingCell }),
    [getBackingCell, rowBreakdowns, rows, step],
  );

  // Counts and metrics come from server-side data covering the whole
  // calculation (step verdict counts, then the criterion summary), never just
  // the pages of rows loaded so far.
  const serverCounts = buildVerdictFilterCounts(verdictCounts, { includeErrorsInFail: false });
  const summaryCounts = useMemo(() => {
    if (!summary) {
      return null;
    }
    const pass = summary.pass_row_count ?? 0;
    const warn = summary.warn_row_count ?? 0;
    const fail = summary.fail_row_count ?? 0;
    const error = summary.error_row_count ?? 0;
    return {
      all: summary.completed_row_count + summary.skipped_row_count + error,
      pass,
      warn,
      fail,
      error,
    };
  }, [summary]);

  const filterCounts = useMemo(() => {
    if (serverCounts) {
      return serverCounts;
    }
    if (summaryCounts) {
      return summaryCounts;
    }
    return countCriterionRowsByFilter(criterionRowItems);
  }, [criterionRowItems, serverCounts, summaryCounts]);

  const completedRows = filterCounts.all;
  const totalRowsForDisplay = Math.max(completedRows, totalRows ?? 0);
  const pendingRows = Math.max(totalRowsForDisplay - completedRows, 0);

  const metrics = useMemo(
    () => ({
      average: summary?.score ?? null,
      passCount: filterCounts.pass,
      errorCount: filterCounts.error,
      completedRows,
    }),
    [completedRows, filterCounts, summary?.score],
  );

  const displayFilterCounts = filterCounts;

  const errorRowCount = summary?.error_row_count ?? 0;
  const hasResults = filterCounts.all > 0 || criterionRowItems.length > 0;

  // Verdict filtering happens server-side (rows arrive pre-filtered); only
  // the label search is applied client-side over the loaded rows.
  const filteredRowItems = useMemo(
    () =>
      criterionRowItems.filter(item =>
        rowMatchesSearchQuery(item.row.row_index, rowListState.search, getRowLabel),
      ),
    [criterionRowItems, getRowLabel, rowListState.search],
  );

  const columnAggregateSourceColumnTitle = useMemo(() => {
    const primitiveConfig = step.primitive_config ?? {};
    const sourceColumnId =
      step.source_column_ids[0] ?? getStringField(primitiveConfig, "source") ?? "";
    return (
      columns.find(column => column.id === sourceColumnId)?.title ?? (sourceColumnId || "Column")
    );
  }, [columns, step.primitive_config, step.source_column_ids]);

  const prefersRowEvidence = columnAggregateUsesExpectedColumn(
    getScorecardColumnAggregateType(step.primitive_config ?? {}),
  );
  const rowResultItems = useMemo(
    () =>
      filteredRowItems.map(item => ({
        rowIndex: item.row.row_index,
        label: getRowLabel?.(item.row.row_index) ?? `Row ${item.row.row_index + 1}`,
        verdict: item.stepResult.verdict,
        score: item.stepResult.score,
        drift: item.stepResult.drift,
        evidence: isColumnAggregate
          ? getColumnAggregateRowEvidence(item.stepResult, columnAggregateSourceColumnTitle, {
              preferEvidence: prefersRowEvidence,
            })
          : getDisplayableEvaluatorEvidence(item.stepResult.evidence),
        errorMessage: item.resolvedError,
        isStale:
          effectiveIsStale && (item.row.stale_state?.stale_step_ids?.includes(step.id) ?? false),
      })),
    [
      columnAggregateSourceColumnTitle,
      prefersRowEvidence,
      effectiveIsStale,
      filteredRowItems,
      getRowLabel,
      isColumnAggregate,
      step.id,
    ],
  );

  const evaluatorRowIndices = useMemo(() => {
    const affectedRows = rows
      .filter(row => {
        const stepResult = rowBreakdowns[row.row_index]?.step_results?.[step.id];
        const isStaleForStep = row.stale_state?.stale_step_ids?.includes(step.id) ?? false;
        if (!stepResult) {
          return isStaleForStep;
        }
        const rowItem = criterionRowItems.find(item => item.row.row_index === row.row_index);
        return isStaleForStep || Boolean(rowItem?.hasError);
      })
      .map(row => row.row_index);

    return affectedRows.length > 0 ? affectedRows : fallbackRerunRowIndices;
  }, [criterionRowItems, fallbackRerunRowIndices, rowBreakdowns, rows, step.id]);

  if (isColumnAggregate) {
    return (
      <ColumnAggregateCriterionDetail
        step={step}
        summary={summary}
        columns={columns}
        rowResultItems={rowResultItems}
        filterCounts={displayFilterCounts}
        rowListState={rowListState}
        onRowListStateChange={onRowListStateChange}
        onOpenRow={onOpenRow}
        onEditCriterion={onEditCriterion}
        onRerun={() => onRerunRows(fallbackRerunRowIndices)}
        isRerunning={isRerunning}
        isRerunDisabled={isRerunDisabled}
        isLoadingRows={isLoadingRows}
        onLoadMoreRows={onLoadMoreRows}
        hasMoreRows={hasMoreRows}
        isLoadingMoreRows={isLoadingMoreRows}
        isStale={effectiveIsStale}
        isReadOnly={isReadOnly}
        titleSlot={titleSlot}
      />
    );
  }

  const metadataLabel = getCriterionMetadataLabel(step, columns);

  return (
    <div className="space-y-3">
      <div className="space-y-0.5">
        <div className="flex items-center justify-between gap-2">
          <div className="min-w-0 flex-1">
            {titleSlot ?? <h3 className={SCORECARD_DETAIL_TITLE}>{step.title}</h3>}
          </div>
          <div className="flex shrink-0 items-center gap-1">
            {!isReadOnly && (
              <Button
                variant="ghost"
                size="sm"
                className="h-6 px-1.5 text-[11px] font-medium text-muted-foreground hover:text-foreground"
                onClick={onEditCriterion}
              >
                <Pencil className="mr-1 h-3 w-3" />
                Edit
              </Button>
            )}
            {hasResults && !effectiveIsStale && !isReadOnly ? (
              <ScorecardRerunTextButton
                onClick={() => onRerunRows(evaluatorRowIndices)}
                isRerunning={isRerunning}
                isDisabled={isRerunDisabled || evaluatorRowIndices.length === 0}
              />
            ) : null}
          </div>
        </div>
        {metadataLabel ? (
          <p className="line-clamp-2 break-words text-xs leading-5 text-muted-foreground">
            {metadataLabel}
          </p>
        ) : null}
      </div>

      {!hasResults && errorRowCount > 0 ? (
        <div className="flex items-start gap-2 rounded-lg border border-red-200 bg-red-50 px-3 py-2.5 text-sm text-red-800">
          <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" />
          <p className="font-medium">
            {errorRowCount} row{errorRowCount === 1 ? "" : "s"} failed to evaluate
          </p>
        </div>
      ) : null}

      {!hasResults ? (
        <ScorecardStaleBanner
          variant="first-run"
          message="Not calculated yet — run this evaluator to score each row."
          actionLabel="Run evaluator"
          loadingLabel="Running…"
          onRecalculate={() => onRerunRows()}
          isRecalculating={isRerunning}
          isDisabled={isRerunDisabled || isReadOnly}
        />
      ) : (
        <>
          {summary?.assertion_summaries?.length ? (
            <div className="space-y-2">
              <ScorecardSectionBlockHeader
                label="Assertions"
                description="Outcome for each AI Judge assertion across the evaluated rows."
              />
              <ScorecardAssertionResults
                assertions={summary.assertion_summaries}
                className="px-0.5"
              />
            </div>
          ) : null}

          <div
            className={cn(
              "flex flex-wrap items-center gap-x-3 gap-y-1.5 rounded-lg border px-3 py-2.5 text-sm text-muted-foreground",
              metrics.errorCount > 0 ? "border-red-200 bg-red-50" : "border-border/70 bg-muted/10",
            )}
          >
            <ScorecardScoreSummary
              score={formatScorecardScore(metrics.average)}
              drift={summary?.drift}
              meta="average"
              scoreClassName="font-medium text-foreground"
              containerClassName="inline-flex items-center gap-1.5 whitespace-nowrap"
            />
            <span className="whitespace-nowrap">
              <span className="font-medium text-foreground">
                {metrics.completedRows > 0
                  ? `${metrics.passCount}/${metrics.completedRows}`
                  : metrics.passCount}
              </span>{" "}
              pass
            </span>
            {pendingRows > 0 ? (
              <span className="whitespace-nowrap text-muted-foreground">
                {completedRows}/{totalRowsForDisplay} scored · {pendingRows} processing
              </span>
            ) : null}
            {metrics.errorCount > 0 ? (
              <span className="whitespace-nowrap text-red-700">
                <span className="font-medium text-red-800">{metrics.errorCount}</span> errors
              </span>
            ) : null}
          </div>

          <div className="space-y-3">
            <ScorecardRowFilterBar
              search={rowListState.search}
              onSearchChange={value => onRowListStateChange({ search: value })}
              filter={rowListState.filter}
              onFilterChange={value => onRowListStateChange({ filter: value })}
              options={CRITERION_ROW_FILTER_OPTIONS}
              counts={displayFilterCounts}
            />

            <ScorecardRowResultsList
              items={rowResultItems}
              onOpenRow={onOpenRow}
              isLoading={isLoadingRows}
              onLoadMore={onLoadMoreRows}
              hasMore={hasMoreRows}
              isLoadingMore={isLoadingMoreRows}
              loadingMessage="Finding matching rows…"
              emptyMessage={getScorecardRowsEmptyMessage(
                rowListState.filter,
                rowListState.search.trim().length > 0,
              )}
            />
          </div>
        </>
      )}
    </div>
  );
};

export default ScorecardCriterionDetailView;
